import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bookmark, BookmarkX, Download, FileSpreadsheet, FileText, Loader2, Plus,
  Search, Share2, UserMinus, Users, X,
} from 'lucide-react';
import { api } from '../utils/api';
import { useIsMobile } from '../utils/useIsMobile';
import OrganicShell from '../components/organic/OrganicShell';
import ShareBrandModal from '../components/ShareBrandModal';

// Following — every brand whose intelligence report has been shared
// with someone (sharing is following: the recipient becomes a
// follower). Two pivots over the same data: by brand (who receives
// each report, growth vs baseline, share/unfollow) and by person
// (which brands each recipient follows). The CRM-leads export lives
// here because it covers exactly these brands.

const STROKE = 2.75;

// The weekly social sync (render.yaml) refreshes follower metrics
// Mondays 09:00 UTC — the moment the numbers on this page move next.
function nextWeeklySync(now = new Date()) {
  const next = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 9, 0, 0,
  ));
  let add = (1 - next.getUTCDay() + 7) % 7;
  if (add === 0 && next <= now) add = 7;
  next.setUTCDate(next.getUTCDate() + add);
  return next;
}

function countdownLabel(to, now = new Date()) {
  const hours = Math.max(1, Math.round((to - now) / 3600000));
  return hours <= 48 ? `${hours}h` : `${Math.round(hours / 24)}d`;
}

function formatNumber(num) {
  if (num == null) return null;
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
}

function formatDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function StatCard({ kicker, value, note, valueStyle, compact }) {
  return (
    <div className="card" style={{ gap: 2, padding: compact ? 'var(--space-3)' : 'var(--space-4)' }}>
      <span className="card-kicker">{kicker}</span>
      <span style={{ fontFamily: 'var(--font-heading)', fontSize: compact ? 26 : 30, ...valueStyle }}>{value}</span>
      <span style={{ fontSize: compact ? 11 : 12, opacity: 0.65 }}>{note}</span>
    </div>
  );
}

function EmptyCard({ title, body }) {
  return (
    <div
      className="card"
      style={{
        padding: 'var(--space-8) var(--space-4)', alignItems: 'center',
        textAlign: 'center', gap: 'var(--space-3)',
      }}
    >
      <span
        style={{
          width: 56, height: 56, borderRadius: 999,
          background: 'var(--color-accent-200)', color: 'var(--color-accent-800)',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Bookmark size={24} strokeWidth={STROKE} />
      </span>
      <h4 style={{ margin: 0 }}>{title}</h4>
      <p style={{ margin: 0, fontSize: 14, opacity: 0.7, maxWidth: 460, textWrap: 'pretty' }}>{body}</p>
      <Link to="/" className="btn btn-secondary" style={{ fontSize: 13 }}>Find brands on Movers</Link>
    </div>
  );
}

function FollowerChips({ brand, busy, onShare, onRemoveFollower }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
      {brand.followers.map((f) => (
        <span
          key={f.id}
          className="tag tag-outline"
          style={{ gap: 5 }}
          title={f.followed_since ? `since ${formatDate(f.followed_since)}` : undefined}
        >
          {f.name || f.email}
          <button
            type="button"
            aria-label={`Remove ${f.name || f.email}`}
            disabled={busy}
            onClick={() => onRemoveFollower(brand, f)}
            style={{
              display: 'inline-flex', padding: 0, background: 'none',
              border: 'none', color: 'inherit', cursor: 'pointer',
            }}
          >
            <X size={11} strokeWidth={STROKE} />
          </button>
        </span>
      ))}
      <button type="button" className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => onShare(brand)}>
        <Plus size={13} strokeWidth={STROKE} /> Add
      </button>
    </div>
  );
}

// Phone card: growth keeps its place beside the name, the recipients sit
// under it, and the two actions take the foot of the card.
function BrandCardCompact({ brand, busy, growthColor, meta, onShare, onRemoveFollower, onUnfollow }) {
  return (
    <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-3)' }}>
        <div style={{ minWidth: 0 }}>
          <Link to={`/brands/${brand.id}`} style={{ fontFamily: 'var(--font-heading)', fontSize: 19, textDecoration: 'none' }}>
            {brand.name}
          </Link>
          {meta && <div style={{ fontSize: 12, opacity: 0.7 }}>{meta}</div>}
        </div>
        <div style={{ textAlign: 'right', flex: 'none' }}>
          <div style={{ fontFamily: 'var(--font-heading)', fontSize: 22, lineHeight: 1, color: growthColor }}>
            {brand.growth_pct != null
              ? `${brand.growth_pct >= 0 ? '+' : ''}${brand.growth_pct.toFixed(1)}%`
              : '—'}
          </div>
          <div style={{ fontSize: 11, opacity: 0.6 }}>
            {brand.baseline_multiple != null
              ? `${brand.baseline_multiple}× baseline`
              : brand.growth_pct != null ? 'at baseline' : 'no weekly data'}
          </div>
        </div>
      </div>

      <FollowerChips brand={brand} busy={busy} onShare={onShare} onRemoveFollower={onRemoveFollower} />

      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <button type="button" className="btn btn-secondary" style={{ flex: 1, height: 44, minWidth: 0 }} onClick={() => onShare(brand)}>
          <Share2 size={15} strokeWidth={STROKE} /> Share again
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-icon"
          style={{ width: 44, height: 44, flex: 'none', color: 'var(--color-accent)' }}
          title="Stop following"
          aria-label={`Stop following ${brand.name}`}
          disabled={busy}
          onClick={() => onUnfollow(brand)}
        >
          {busy
            ? <Loader2 size={16} strokeWidth={STROKE} className="spin" />
            : <BookmarkX size={16} strokeWidth={STROKE} />}
        </button>
      </div>
    </div>
  );
}

function BrandCard({ brand, busy, compact, onShare, onRemoveFollower, onUnfollow }) {
  const europe = brand.region === 'Europe';
  const growthColor = europe ? 'var(--color-accent-2-700)' : 'var(--color-accent-700)';
  const followedSince = formatDate(
    brand.followers.reduce(
      (min, f) => (f.followed_since && (!min || f.followed_since < min) ? f.followed_since : min),
      null,
    ),
  );
  const meta = [
    [brand.hq_city, brand.country].filter(Boolean).join(', ') || null,
    brand.current_followers != null ? `${formatNumber(brand.current_followers)} followers` : null,
    followedSince ? `followed ${followedSince}` : null,
  ].filter(Boolean).join(' · ');
  const n = brand.followers.length;

  if (compact) {
    return (
      <BrandCardCompact
        brand={brand}
        busy={busy}
        growthColor={growthColor}
        meta={meta}
        onShare={onShare}
        onRemoveFollower={onRemoveFollower}
        onUnfollow={onUnfollow}
      />
    );
  }

  return (
    <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)', flexWrap: 'wrap' }}>

        <div style={{ minWidth: 0, flex: '1 1 380px', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <Link to={`/brands/${brand.id}`} style={{ fontFamily: 'var(--font-heading)', fontSize: 20, textDecoration: 'none' }}>
              {brand.name}
            </Link>
            {brand.category && <span className="tag tag-neutral">{brand.category}</span>}
            {brand.region && (
              <span className={europe ? 'tag tag-accent-2' : 'tag tag-accent'}>{brand.region}</span>
            )}
          </div>
          {meta && <div style={{ fontSize: 13, opacity: 0.7 }}>{meta}</div>}

          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap', paddingTop: 'var(--space-1)' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
              <Users size={14} strokeWidth={STROKE} style={{ opacity: 0.6 }} />
              {n === 1 ? '1 person receives this' : `${n} people receive this`}
            </span>
            <FollowerChips
              brand={brand}
              busy={busy}
              onShare={onShare}
              onRemoveFollower={onRemoveFollower}
            />
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flex: 'none' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 26, lineHeight: 1, color: growthColor }}>
              {brand.growth_pct != null
                ? `${brand.growth_pct >= 0 ? '+' : ''}${brand.growth_pct.toFixed(1)}%`
                : '—'}
            </div>
            <div style={{ fontSize: 11, opacity: 0.6 }}>
              {brand.baseline_multiple != null
                ? `${brand.baseline_multiple}× baseline`
                : brand.growth_pct != null ? 'at baseline' : 'no weekly data yet'}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', minWidth: 150 }}>
            <button type="button" className="btn btn-secondary" style={{ fontSize: 13 }} onClick={() => onShare(brand)}>
              <Share2 size={14} strokeWidth={STROKE} /> Share again
            </button>
            <button type="button" className="btn btn-ghost" style={{ fontSize: 13 }} disabled={busy} onClick={() => onUnfollow(brand)}>
              {busy
                ? <Loader2 size={14} strokeWidth={STROKE} className="spin" />
                : <BookmarkX size={14} strokeWidth={STROKE} />} Stop following
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PersonCard({ person, busy, onRemoveAll }) {
  return (
    <div className="card" style={{ padding: 'var(--space-4)', gap: 'var(--space-3)' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 380px', display: 'flex', alignItems: 'flex-start', gap: 'var(--space-3)' }}>
          <span
            style={{
              width: 40, height: 40, flex: 'none', borderRadius: 999,
              background: person.isUser ? 'var(--color-accent-2-300)' : 'var(--color-accent-300)',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 15, fontWeight: 700,
            }}
          >
            {(person.name || person.email).charAt(0).toUpperCase()}
          </span>
          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 15 }}>{person.name || person.email}</strong>
              <span className={person.isUser ? 'tag tag-neutral' : 'tag tag-outline'}>
                {person.isUser ? 'Colleague' : 'External'}
              </span>
            </div>
            <span style={{ fontSize: 13, opacity: 0.7 }}>
              {person.email}
              {person.since ? ` · following since ${formatDate(person.since)}` : ''}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
              {person.brands.map((b) => (
                <Link key={b.id} to={`/brands/${b.id}`} className="tag tag-accent-2" style={{ textDecoration: 'none' }}>
                  {b.name}
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', flex: 'none' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: 'var(--font-heading)', fontSize: 26, lineHeight: 1 }}>{person.brands.length}</div>
            <div style={{ fontSize: 11, opacity: 0.6 }}>{person.brands.length === 1 ? 'brand' : 'brands'}</div>
          </div>
          <button type="button" className="btn btn-ghost" style={{ fontSize: 13 }} disabled={busy} onClick={() => onRemoveAll(person)}>
            {busy
              ? <Loader2 size={14} strokeWidth={STROKE} className="spin" />
              : <UserMinus size={14} strokeWidth={STROKE} />} Remove from all
          </button>
        </div>
      </div>
    </div>
  );
}

function Following() {
  const isMobile = useIsMobile();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pivot, setPivot] = useState('brand');
  const [query, setQuery] = useState('');
  const [share, setShare] = useState(null); // { brand } | { picker: true }
  const [brandOptions, setBrandOptions] = useState(null);
  const [busy, setBusy] = useState({}); // brand id / person email → true
  const [exportOpen, setExportOpen] = useState(false);
  const [exporting, setExporting] = useState(null); // 'csv' | 'xlsx' while downloading

  const load = useCallback(async () => {
    try {
      const result = await api.getFollowedBrands();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const brands = useMemo(() => data?.brands || [], [data]);

  // One entry per distinct recipient email, across all followed brands.
  const people = useMemo(() => {
    const byEmail = new Map();
    for (const b of brands) {
      for (const f of b.followers) {
        const key = (f.email || '').trim().toLowerCase();
        if (!key) continue;
        const p = byEmail.get(key) || {
          email: f.email, name: null, isUser: false, since: null, brands: [], rows: [],
        };
        if (!p.name && f.name) p.name = f.name;
        if (f.is_user) p.isUser = true;
        if (f.followed_since && (!p.since || f.followed_since < p.since)) p.since = f.followed_since;
        p.brands.push({ id: b.id, name: b.name });
        p.rows.push({ brandId: b.id, followerId: f.id });
        byEmail.set(key, p);
      }
    }
    return [...byEmail.values()].sort(
      (a, b) => (a.name || a.email).localeCompare(b.name || b.email),
    );
  }, [brands]);

  const byBrand = pivot === 'brand';
  const q = query.trim().toLowerCase();
  const visibleBrands = useMemo(() => brands.filter(
    (b) => !q
      || b.name.toLowerCase().includes(q)
      || (b.category || '').toLowerCase().includes(q),
  ), [brands, q]);
  const visiblePeople = useMemo(() => people.filter(
    (p) => !q
      || (p.name || '').toLowerCase().includes(q)
      || p.email.toLowerCase().includes(q),
  ), [people, q]);

  const colleagues = people.filter((p) => p.isUser).length;
  const external = people.length - colleagues;
  const moving = brands.filter((b) => (b.baseline_multiple || 0) >= 1.3).length;
  const syncAt = nextWeeklySync();

  const setRowBusy = (key, value) => setBusy((prev) => ({ ...prev, [key]: value }));

  const dropFollowerRows = (removed) => {
    // removed: Set of "brandId:followerId" — prune locally, dropping
    // brands left with nobody.
    setData((prev) => prev && {
      ...prev,
      brands: prev.brands
        .map((b) => ({
          ...b,
          followers: b.followers.filter((f) => !removed.has(`${b.id}:${f.id}`)),
        }))
        .filter((b) => b.followers.length > 0),
    });
  };

  const removeFollower = async (brand, follower) => {
    setRowBusy(brand.id, true);
    try {
      await api.removeBrandFollower(brand.id, follower.id);
      dropFollowerRows(new Set([`${brand.id}:${follower.id}`]));
    } catch (err) {
      setError(err.message);
    } finally {
      setRowBusy(brand.id, false);
    }
  };

  const unfollowBrand = async (brand) => {
    setRowBusy(brand.id, true);
    try {
      await Promise.all(brand.followers.map(
        (f) => api.removeBrandFollower(brand.id, f.id),
      ));
      dropFollowerRows(new Set(brand.followers.map((f) => `${brand.id}:${f.id}`)));
    } catch (err) {
      setError(err.message);
      load(); // partial removals — resync with the server
    } finally {
      setRowBusy(brand.id, false);
    }
  };

  const removePersonEverywhere = async (person) => {
    setRowBusy(person.email, true);
    try {
      await Promise.all(person.rows.map(
        (r) => api.removeBrandFollower(r.brandId, r.followerId),
      ));
      dropFollowerRows(new Set(person.rows.map((r) => `${r.brandId}:${r.followerId}`)));
    } catch (err) {
      setError(err.message);
      load();
    } finally {
      setRowBusy(person.email, false);
    }
  };

  // Export format picker. The button opens the menu; picking a format
  // is what actually starts the download.
  const exportRef = useRef(null);
  useEffect(() => {
    if (!exportOpen) return undefined;
    const onClick = (e) => {
      if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setExportOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [exportOpen]);

  const openSharePicker = () => {
    setShare({ picker: true });
    if (!brandOptions) {
      api.getBrands({ limit: 1000 })
        .then((resp) => setBrandOptions(
          (resp.brands || [])
            .map((b) => ({ id: b.id, name: b.name }))
            .sort((a, b) => a.name.localeCompare(b.name)),
        ))
        .catch(() => setBrandOptions([]));
    }
  };

  const exportCrm = async (format) => {
    setExportOpen(false);
    setExporting(format);
    try {
      const response = await api.exportCrmLeads(format);
      if (!response.ok) throw new Error(`CRM export failed (${response.status})`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `crm_leads.${format}`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(null);
    }
  };

  const resultNote = byBrand
    ? `${visibleBrands.length} ${visibleBrands.length === 1 ? 'brand' : 'brands'}`
    : `${visiblePeople.length} ${visiblePeople.length === 1 ? 'person' : 'people'}`;

  const emptyEverything = (
    <EmptyCard
      title="You are not following anything yet."
      body="Share a brand report with a colleague and it lands here — the recipient becomes a follower, and you get a running list of what your team is watching."
    />
  );

  return (
    <OrganicShell active="following" contentStyle={{ maxWidth: 1240 }}>
      <header style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 'var(--space-4) var(--space-6)', flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: '1 1 340px' }}>
          <h1 className="o-page-title">Following</h1>
          {!isMobile && (
            <p className="o-page-subhead" style={{ textWrap: 'pretty' }}>
              Brands whose intelligence report has gone out to someone on the
              team. Following a brand is the same action as sharing it &mdash;
              the recipient becomes a follower.
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 'var(--space-2)', flex: 'none', flexWrap: 'wrap' }}>
          {/* The export drops to an icon on a phone so Share keeps its label. */}
          <div style={{ position: 'relative', flex: 'none' }} ref={exportRef}>
            <button
              type="button"
              className={`btn btn-secondary${isMobile ? ' btn-icon' : ''}`}
              style={isMobile ? { width: 44, height: 44 } : undefined}
              title="Export CRM leads"
              aria-label="Export CRM leads"
              aria-haspopup="menu"
              aria-expanded={exportOpen}
              disabled={!!exporting}
              onClick={() => setExportOpen((v) => !v)}
            >
              {exporting
                ? <Loader2 size={16} strokeWidth={STROKE} className="spin" />
                : <Download size={16} strokeWidth={STROKE} />}
              {!isMobile && ' Export CRM leads'}
            </button>
            {exportOpen && (
              <div
                className="card elev-lg"
                role="menu"
                style={{
                  position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 50,
                  padding: 'var(--space-2)', minWidth: 200, gap: 2,
                  background: 'var(--color-neutral-100)', border: '1px solid var(--color-divider)',
                }}
              >
                <button
                  type="button"
                  role="menuitem"
                  className="o-account-menu-item"
                  onClick={() => exportCrm('csv')}
                >
                  <FileText size={14} strokeWidth={STROKE} /> CSV (.csv)
                </button>
                <button
                  type="button"
                  role="menuitem"
                  className="o-account-menu-item"
                  onClick={() => exportCrm('xlsx')}
                >
                  <FileSpreadsheet size={14} strokeWidth={STROKE} /> Excel (.xlsx)
                </button>
              </div>
            )}
          </div>
          <button
            type="button"
            className="btn btn-primary"
            style={isMobile ? { height: 44 } : undefined}
            onClick={openSharePicker}
          >
            <Share2 size={16} strokeWidth={STROKE} /> Share{!isMobile && ' a brand'}
          </button>
        </div>
      </header>

      {/* Four tiles won't stack readably on a phone — they become a
          swipeable strip instead (see .o-stat-strip). */}
      <div className="o-stat-strip">
        <StatCard
          compact={isMobile}
          kicker="Brands followed"
          value={brands.length}
          note={data?.watched_count != null ? `of ${data.watched_count} watched` : ' '}
        />
        <StatCard
          compact={isMobile}
          kicker="People receiving"
          value={people.length}
          note={people.length
            ? `${colleagues} ${colleagues === 1 ? 'colleague' : 'colleagues'}, ${external} external`
            : 'nobody yet'}
        />
        <StatCard
          compact={isMobile}
          kicker="Moving this week"
          value={moving}
          note="ahead of their baseline"
          valueStyle={{ color: 'var(--color-accent-2-700)' }}
        />
        <StatCard
          compact={isMobile}
          kicker="Next sync"
          value={countdownLabel(syncAt)}
          note="Monday 09:00 UTC"
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <div className={`seg${isMobile ? ' o-seg-wide' : ''}`} style={isMobile ? undefined : { flex: 'none' }}>
          <label className="seg-opt" style={{ whiteSpace: 'nowrap' }}>
            <input
              type="radio"
              name="pivot"
              checked={byBrand}
              onChange={() => { setPivot('brand'); setQuery(''); }}
            />
            By brand
          </label>
          <label className="seg-opt" style={{ whiteSpace: 'nowrap' }}>
            <input
              type="radio"
              name="pivot"
              checked={!byBrand}
              onChange={() => { setPivot('person'); setQuery(''); }}
            />
            By person
          </label>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flex: '1 1 240px', minWidth: 0 }}>
          <Search size={16} strokeWidth={STROKE} style={{ opacity: 0.55, flex: 'none' }} />
          <input
            className="input"
            placeholder={byBrand ? 'Search followed brands…' : 'Search people by name or email…'}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={byBrand ? 'Search followed brands' : 'Search people'}
          />
        </div>
        <span style={{ fontSize: 13, opacity: 0.6 }}>{resultNote}</span>
      </div>

      {error && (
        <div className="card" style={{ padding: 'var(--space-3)', color: 'var(--color-accent-700)', fontSize: 14 }}>
          Error: {error}
        </div>
      )}

      {loading ? (
        <div className="card" style={{ padding: 'var(--space-4)', flexDirection: 'row', alignItems: 'center', gap: 8, fontSize: 14, opacity: 0.75 }}>
          <Loader2 size={16} strokeWidth={STROKE} className="spin" /> Loading followed brands…
        </div>
      ) : byBrand ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {visibleBrands.map((b) => (
            <BrandCard
              key={b.id}
              brand={b}
              busy={!!busy[b.id]}
              compact={isMobile}
              onShare={(brand) => setShare({ brand })}
              onRemoveFollower={removeFollower}
              onUnfollow={unfollowBrand}
            />
          ))}
          {visibleBrands.length === 0 && (q ? (
            <EmptyCard
              title={`No followed brand matches “${query.trim()}”.`}
              body="Try a brand name or a category, or switch to the people view."
            />
          ) : emptyEverything)}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {visiblePeople.map((p) => (
            <PersonCard
              key={p.email.toLowerCase()}
              person={p}
              busy={!!busy[p.email]}
              onRemoveAll={removePersonEverywhere}
            />
          ))}
          {visiblePeople.length === 0 && (q ? (
            <div className="card" style={{ padding: 'var(--space-8) var(--space-4)', alignItems: 'center', textAlign: 'center', gap: 'var(--space-3)' }}>
              <h4 style={{ margin: 0 }}>Nobody matches &ldquo;{query.trim()}&rdquo;.</h4>
              <p style={{ margin: 0, fontSize: 14, opacity: 0.7 }}>Search by name or email address.</p>
            </div>
          ) : emptyEverything)}
        </div>
      )}

      {share && (
        <ShareBrandModal
          brand={share.brand || null}
          brandOptions={share.brand ? undefined : brandOptions}
          onClose={() => setShare(null)}
          onShared={load}
        />
      )}
    </OrganicShell>
  );
}

export default Following;
